import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";
import { useIsMobile } from "@/hooks/use-mobile";
import logo from "@/assets/logo.png";
import PromoBanner from "@/components/layout/PromoBanner";
const navLinks = [
  { name: "Cabanas", path: "/cabanas" },
  { name: "Activities", path: "/activities" },
  { name: "Island Map", path: "/island-map" },
  { name: "Blog", path: "/blog" },
  { name: "FAQ", path: "/faq" },
  { name: "Gift Cards", path: "/gift-cards" },
  { name: "Contact", path: "/contact" },
];

const mobileOnlyLinks = [
  { name: "About", path: "/about" },
  { name: "Other Properties", path: "/other-properties" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);
  const location = useLocation();
  const isMobile = useIsMobile();
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Mobile-only: hide banner on scroll, show again after 3s of no scrolling
  useEffect(() => {
    if (!isMobile) {
      setBannerVisible(true);
      return;
    }

    const handleScroll = () => {
      if (window.scrollY <= 20) {
        setBannerVisible(true);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = null;
        }
        return;
      }

      setBannerVisible(false);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        setBannerVisible(true);
      }, 3000);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [isMobile]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Internal curation tool — hide global header + announcement banner.
  if (location.pathname.startsWith("/images5")) {
    return null;
  }

  return (
    <div className="sticky top-0 z-50">
      {/* Announcement Banner — collapses on scroll (mobile), reappears after 3s idle */}
      <motion.div
        initial={false}
        animate={{
          height: bannerVisible ? "auto" : 0,
          opacity: bannerVisible ? 1 : 0,
        }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <PromoBanner />
      </motion.div>

      {/* Main Header */}
      <header
        className={`transition-all duration-300 ${
          isScrolled
            ? "bg-background/95 backdrop-blur-md shadow-soft"
            : "bg-background/80 backdrop-blur-sm"
        }`}
      >
        <div className="container mx-auto">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-1.5">
              <img 
                src={logo} 
                alt="Son's Island Texas" 
                className="h-12 lg:h-16 w-auto"
              />
              <div className="flex flex-col">
                <span className="font-display text-xl lg:text-2xl font-bold text-primary tracking-tight">
                  Son's Island
                </span>
                <span className="text-[10px] lg:text-xs text-muted-foreground -mt-1 tracking-wider">
                  PRIVATE ISLAND RESORT
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.path
                      ? "text-primary bg-primary/5"
                      : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="tel:877-577-7667"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Phone className="w-4 h-4" />
                877-577-7667
              </a>
              <LanguageSelector variant="icon" />
              <Button variant="hero" asChild>
                <Link to="/book-now">Book Now</Link>
              </Button>
            </div>

            {/* Mobile/Tablet Actions */}
            <div className="flex lg:hidden items-center gap-2">
              <Button variant="hero" size="sm" asChild>
                <Link to="/book-now">Book Now</Link>
              </Button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-background border-t border-border overflow-hidden"
            >
              <nav className="container py-4 flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                      location.pathname === link.path
                        ? "text-primary bg-primary/5"
                        : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="border-t border-border my-2" />
                {mobileOnlyLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                      location.pathname === link.path
                        ? "text-primary bg-primary/5"
                        : "text-foreground/70 hover:text-primary hover:bg-primary/5"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="border-t border-border my-2" />
                <a
                  href="tel:877-577-7667"
                  className="flex items-center gap-2 px-4 py-3 text-muted-foreground"
                >
                  <Phone className="w-4 h-4" />
                  877-577-7667
                </a>
                <LanguageSelector variant="full" />
                <Button variant="hero" className="mx-4 mt-2" asChild>
                  <Link to="/book-now">Book Now</Link>
                </Button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </div>
  );
};

export default Header;
